import { SubscriptionHistoryState, UpdateSubscription } from './../models/shop';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { print } from 'graphql';
import request from '../utils/request';
import { RootState } from '../utils/store';
import { GET_SUBSCRIPTION_HISTORY } from './../utils/queries';
import {
  CHANGE_STATUS_SUBSCRIPTION,
  UPDATE_SUBSCRIPTION,
} from '../utils/mutations';
import { signOut } from './user';

export const getSubscriptionsHistory = createAsyncThunk(
  'subscriptions/getSubscriptionHistory',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await request.get('graphql', {
        params: {
          query: print(GET_SUBSCRIPTION_HISTORY),
        },
      });
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const changeStatusSubscription = createAsyncThunk(
  'subscriptions/changeStatusSubscription',
  async (
    params: { entity_id: number; status: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { data } = await request.post('graphql', {
        query: print(CHANGE_STATUS_SUBSCRIPTION),
        variables: { entity_id: params.entity_id, status: params.status },
      });
      if (data.errors) {
        return rejectWithValue(data.errors);
      }
      dispatch(getSubscriptionsHistory());
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'subscriptions/updateSubscription',
  async (input: UpdateSubscription, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await request.post('graphql', {
        query: print(UPDATE_SUBSCRIPTION),
        variables: {
          entity_id: input.entity_id,
          billing_address: input.billing_address,
          shipping_address: input.shipping_address,
        },
      });
      if (data.errors) {
        return rejectWithValue(data.errors);
      }
      dispatch(getSubscriptionsHistory());
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getListSquareSubscription = createAsyncThunk(
  'subscriptions/getListSquareSubscription',
  async (
    payload: {
      email: string;
      storeCode: string;
      isShopGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.get(
        `/rest/${payload.storeCode}/V1/square/subscription/get-by-customer?customer_email=${payload.email}`,
        {
          params: { isShopGolftec: payload.isShopGolftec },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getDetailGTShopSubscription = createAsyncThunk(
  'subscriptions/getDetailGTShopSubscription',
  async (
    payload: {
      id: string;
      storeCode: string;
      isShopGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.get(
        `/rest/${payload.storeCode}/V1/square/subscription/get-by-id?id=${payload.id}`,
        {
          params: { isShopGolftec: payload.isShopGolftec },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const skipOnceSubscriptionGTShop = createAsyncThunk(
  'subscriptions/skipOnceSubscriptionGTShop',
  async (
    payload: {
      id: string;
      storeCode: string;
      isShopGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `/rest/${payload.storeCode}/V1/square/subscription/gtshop/skip-once`,
        { subscription_id: payload.id },
        {
          params: { isShopGolftec: payload.isShopGolftec },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const cancelSubscriptionGTShop = createAsyncThunk(
  'subscriptions/cancelSubscriptionGTShop',
  async (
    payload: {
      id: string;
      storeCode: string;
      isShopGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `/rest/${payload.storeCode}/V1/square/subscription/gtshop/cancel`,
        { subscription_id: payload.id },
        {
          params: { isShopGolftec: payload.isShopGolftec },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const changeShipDateSubscriptionGTShop = createAsyncThunk(
  'subscriptions/changeShipDateSubscriptionGTShop',
  async (
    payload: {
      id: string;
      storeCode: string;
      shipDate: string;
      isShopGolftec: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await request.post(
        `/rest/${payload.storeCode}/V1/square/subscription/gtshop/reschedule-ship-date`,
        { subscription_id: payload.id, ship_date: payload.shipDate },
        {
          params: { isShopGolftec: payload.isShopGolftec },
        }
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error?.response?.data);
    }
  }
);

const initialState: SubscriptionHistoryState = {
  loadingSupbscription: false,
  subscriptionHistoryData: null,
  timeZone: '',
  listSubscriptionGTShop: [],
  loadingListSubscriptionGTShop: false,
  hasFetchListSubscriptionGTShop: false,
  loadingDetailSubscriptionGTShop: [],
  listDetailSubscriptionGTShop: [],
};

const subscriptionsHistorySlice = createSlice({
  name: 'subscriptionsHistory',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionsHistory.pending, (state, action) => {
        state.loadingSupbscription = true;
      })
      .addCase(getSubscriptionsHistory.fulfilled, (state, action) => {
        state.loadingSupbscription = false;
        if (action.payload.data) {
          state.subscriptionHistoryData = action.payload.data.subscriptions;
          state.timeZone = action.payload.data.storeConfig.timeZone;
        }
      })
      .addCase(getSubscriptionsHistory.rejected, (state, action) => {
        state.loadingSupbscription = false;
      })
      .addCase(getListSquareSubscription.pending, (state) => {
        if (!state.hasFetchListSubscriptionGTShop) {
          state.loadingListSubscriptionGTShop = true;
        }
      })
      .addCase(getListSquareSubscription.fulfilled, (state, action) => {
        state.loadingListSubscriptionGTShop = false;
        state.hasFetchListSubscriptionGTShop = true;
        if (action.payload.items) {
          state.listSubscriptionGTShop = action.payload.items;
        }
      })
      .addCase(getDetailGTShopSubscription.pending, (state, action) => {
        state.loadingDetailSubscriptionGTShop.push(action.meta.arg.id);
      })
      .addCase(getDetailGTShopSubscription.fulfilled, (state, action) => {
        state.loadingDetailSubscriptionGTShop =
          state.loadingDetailSubscriptionGTShop.filter(
            (id) => id !== action.meta.arg.id
          );
        if (action.payload.subscription_id) {
          const idx = state.listDetailSubscriptionGTShop.findIndex(
            (item) => item.subscription_id === action.payload.subscription_id
          );
          if (idx >= 0) {
            state.listDetailSubscriptionGTShop[idx] = action.payload;
          } else {
            state.listDetailSubscriptionGTShop.push(action.payload);
          }
        }
      })
      .addCase(signOut, () => ({ ...initialState }));
  },
});

export const selectSubscriptionsHistory = (state: RootState) =>
  state.subscriptionHistory.subscriptionHistoryData;
export const loadingSubscriptionsHistory = (state: RootState) =>
  state.subscriptionHistory.loadingSupbscription;
export const selectTimeZoneSubscription = (state: RootState) =>
  state.subscriptionHistory.timeZone;
export const selectListSubscriptionGTShop = (state: RootState) =>
  state.subscriptionHistory.listSubscriptionGTShop;
export const selectLoadingSubscriptionGTShop = (state: RootState) =>
  state.subscriptionHistory.loadingListSubscriptionGTShop;
export const selectListDetailSubscriptionGTShop = (state: RootState) =>
  state.subscriptionHistory.listDetailSubscriptionGTShop;
export const selectLoadingDetailSubscriptionGTShop = (state: RootState) =>
  state.subscriptionHistory.loadingDetailSubscriptionGTShop;

export default subscriptionsHistorySlice.reducer;
